import { supabase } from './supabase';

/**
 * The long-form prompts a CTO review runs with.
 *
 * One row per subject, written as prose rather than split into fields. Children are deeper
 * passes that only run when their trigger is met — "Stripe is in the dependencies" — which
 * is why the trigger is prose too: the model doing the detecting reads it directly.
 *
 * See supabase/migrations/20260904000000_cto_prompts.sql.
 */

export const CTO_PROMPT_ROLES = ['framework', 'system'] as const;
export type CtoPromptRole = (typeof CTO_PROMPT_ROLES)[number];

export const CTO_PROMPT_STATUSES = ['draft', 'active', 'retired'] as const;
export type CtoPromptStatus = (typeof CTO_PROMPT_STATUSES)[number];

/** Front-of-house CTO MCP tools a framework can point at. */
export const CTO_FOH_TOOLS = [
  'cto_code_review',
  'cto_review_prompt',
  'cto_stack_review',
  'cto_list_reviews',
  'cto_get_knowns',
  'cto_record_findings',
  'cto_record_knowns',
  'cto_whoami',
] as const;
export type CtoFohTool = (typeof CTO_FOH_TOOLS)[number];

export function isCtoFohTool(value: string): value is CtoFohTool {
  return (CTO_FOH_TOOLS as readonly string[]).includes(value);
}

export interface CtoPromptRow {
  id: string;
  parentId: string | null;
  slug: string;
  title: string;
  role: CtoPromptRole;
  /** Null on a top-level prompt means it always runs. */
  triggerMd: string | null;
  bodyMd: string;
  /** Front-of-house CTO MCP tool this framework drives. */
  fohTool: string | null;
  /** When the advisor should reach for this framework. */
  whenMd: string | null;
  /** Why that tool is the next useful action. */
  whyMd: string | null;
  position: number;
  status: CtoPromptStatus;
  createdAt: string;
  updatedAt: string;
  children: CtoPromptRow[];
}

export interface CtoPromptInput {
  parentId?: string | null;
  slug: string;
  title: string;
  role?: CtoPromptRole;
  triggerMd?: string | null;
  bodyMd?: string;
  fohTool?: string | null;
  whenMd?: string | null;
  whyMd?: string | null;
  position?: number;
  status?: CtoPromptStatus;
}

function mapRow(r: Record<string, unknown>): CtoPromptRow {
  return {
    id: String(r.id),
    parentId: r.parent_id ? String(r.parent_id) : null,
    slug: String(r.slug),
    title: String(r.title),
    role: (r.role as CtoPromptRole) ?? 'framework',
    triggerMd: r.trigger_md ? String(r.trigger_md) : null,
    bodyMd: typeof r.body_md === 'string' ? r.body_md : '',
    fohTool: r.foh_tool ? String(r.foh_tool) : null,
    whenMd: r.when_md ? String(r.when_md) : null,
    whyMd: r.why_md ? String(r.why_md) : null,
    position: Number(r.position ?? 0),
    status: (r.status as CtoPromptStatus) ?? 'draft',
    createdAt: String(r.created_at),
    updatedAt: String(r.updated_at),
    children: [],
  };
}

/**
 * Returned as a tree rather than a flat list. Depth is not capped in the schema, so this
 * nests to whatever exists rather than assuming one level of children.
 */
export async function fetchCtoPrompts(): Promise<CtoPromptRow[]> {
  const { data, error } = await supabase
    .from('cto_prompts')
    .select('*')
    .order('position', { ascending: true })
    .order('title', { ascending: true });
  if (error) throw new Error(error.message);

  const rows = (data ?? []).map((r) => mapRow(r as Record<string, unknown>));
  const byId = new Map(rows.map((r) => [r.id, r]));
  const roots: CtoPromptRow[] = [];

  for (const row of rows) {
    const parent = row.parentId ? byId.get(row.parentId) : undefined;
    if (parent) parent.children.push(row);
    else roots.push(row);
  }
  return roots;
}

export async function createPrompt(input: CtoPromptInput): Promise<CtoPromptRow> {
  const { data, error } = await supabase
    .from('cto_prompts')
    .insert({
      parent_id: input.parentId ?? null,
      slug: input.slug,
      title: input.title,
      role: input.role ?? 'framework',
      trigger_md: input.triggerMd ?? null,
      body_md: input.bodyMd ?? '',
      foh_tool: input.fohTool ?? null,
      when_md: input.whenMd ?? null,
      why_md: input.whyMd ?? null,
      position: input.position ?? 0,
      status: input.status ?? 'draft',
    })
    .select('*')
    .single();
  if (error) throw new Error(error.message);
  return mapRow(data as Record<string, unknown>);
}

export async function updatePrompt(
  id: string,
  patch: Partial<CtoPromptInput>,
): Promise<CtoPromptRow> {
  const payload: Record<string, unknown> = {};
  if (patch.slug !== undefined) payload.slug = patch.slug;
  if (patch.title !== undefined) payload.title = patch.title;
  if (patch.role !== undefined) payload.role = patch.role;
  if (patch.triggerMd !== undefined) payload.trigger_md = patch.triggerMd;
  if (patch.bodyMd !== undefined) payload.body_md = patch.bodyMd;
  if (patch.fohTool !== undefined) payload.foh_tool = patch.fohTool;
  if (patch.whenMd !== undefined) payload.when_md = patch.whenMd;
  if (patch.whyMd !== undefined) payload.why_md = patch.whyMd;
  if (patch.position !== undefined) payload.position = patch.position;
  if (patch.status !== undefined) payload.status = patch.status;
  if (patch.parentId !== undefined) payload.parent_id = patch.parentId;

  const { data, error } = await supabase
    .from('cto_prompts')
    .update(payload)
    .eq('id', id)
    .select('*')
    .single();
  if (error) throw new Error(error.message);
  return mapRow(data as Record<string, unknown>);
}

/** Children cascade. Nothing references these rows, so a bad idea leaves rather than lingers. */
export async function deletePrompt(id: string): Promise<void> {
  const { error } = await supabase.from('cto_prompts').delete().eq('id', id);
  if (error) throw new Error(error.message);
}

export function findPrompt(roots: CtoPromptRow[], id: string): CtoPromptRow | null {
  for (const r of roots) {
    if (r.id === id) return r;
    const hit = findPrompt(r.children, id);
    if (hit) return hit;
  }
  return null;
}

export interface FlatPrompt {
  prompt: CtoPromptRow;
  depth: number;
}

/**
 * Depth-first, so a child sits directly under its parent in a flat table. Indentation is
 * what carries the hierarchy — a tree widget would be a lot of interaction for a list this
 * size.
 */
export function flattenPrompts(roots: CtoPromptRow[], depth = 0): FlatPrompt[] {
  return roots.flatMap((p) => [{ prompt: p, depth }, ...flattenPrompts(p.children, depth + 1)]);
}

export function slugFromTitle(title: string): string {
  return title
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 96);
}

export function isValidSlug(slug: string): boolean {
  return /^[a-z0-9]+(-[a-z0-9]+)*$/.test(slug) && slug.length >= 3 && slug.length <= 96;
}

/** Sparse, so a prompt can later be inserted between two others without renumbering. */
export function nextPosition(siblings: CtoPromptRow[]): number {
  if (siblings.length === 0) return 10;
  return Math.max(...siblings.map((s) => s.position)) + 10;
}

export function countPrompts(roots: CtoPromptRow[]): number {
  return roots.reduce((n, r) => n + 1 + countPrompts(r.children), 0);
}

export type PromptUsage = {
  slug: string;
  findings: number;
  staged: number;
};

/**
 * Which briefs earn their keep: findings attributed to a slug, and how many
 * made the short list. Unused slugs are absent from the map.
 */
export async function fetchPromptUsage(): Promise<Map<string, PromptUsage>> {
  const { data, error } = await supabase
    .from('cto_findings')
    .select('prompt_slug, staged')
    .not('prompt_slug', 'is', null);
  if (error) throw new Error(error.message);

  const bySlug = new Map<string, PromptUsage>();
  for (const row of data ?? []) {
    const slug = typeof row.prompt_slug === 'string' ? row.prompt_slug : '';
    if (!slug) continue;
    const current = bySlug.get(slug) ?? { slug, findings: 0, staged: 0 };
    current.findings += 1;
    if (row.staged) current.staged += 1;
    bySlug.set(slug, current);
  }
  return bySlug;
}
